import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, Icons, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';
import {
  extractEnumMapFromFieldConfig,
  getLabelForEnumKey,
  getReformedTitle,
} from '../../util/genericHelpers';
import {
  BOARD_FOOTAGE_FIELD,
  calculateApproximateBoardFootage,
  formatBoardFootage,
} from '../../util/boardFootage';

import {
  addItemToFavorites,
  deleteItemFromFavorites,
  favoritesSelector,
} from '../../containers/FavoritesPage/FavoritesPage.duck';
import { useDispatch, useSelector } from 'react-redux';
import { isAuthenticatedSelector } from '../../ducks/auth.duck';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const dispatch = useDispatch();
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const { favorites = [] } = useSelector(favoritesSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);

  const isAlreadyAddedToFavorites = Array.isArray(favorites) && favorites.includes(id);

  const handleFavorites = async type => {
    const action = type === 'unlike' ? deleteItemFromFavorites : addItemToFavorites;
    const payload =
      type === 'unlike'
        ? { isAuthenticated, id, favorites, config }
        : { favorites, selectedFavoriteId: id };

    try {
      await dispatch(action(payload));
    } catch (error) {
      console.error('Error handling favorites');
    }
  };

  const { Length, Narrowest_Measurement, Widest_Measurement, thickness } = publicData || {};

  const thicknessField = config.listing.listingFields.find(f => f.key === 'thickness');
  const thicknessMap = extractEnumMapFromFieldConfig(thicknessField);

  const thinknessLabel = getLabelForEnumKey(thicknessMap, publicData?.thickness);
  const approximateBoardFootage =
    publicData?.[BOARD_FOOTAGE_FIELD] ??
    calculateApproximateBoardFootage(publicData, config.listing.listingFields);
  const boardFootageLabel = formatBoardFootage(approximateBoardFootage);

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge']}
          sizes={renderSizes}
        />
        <div className={css.favorite}>
          <div
            className={classNames(isAlreadyAddedToFavorites ? css.filled : css.icon)}
            onClick={e => {
              handleFavorites(isAlreadyAddedToFavorites ? 'unlike' : 'like');
              e.preventDefault();
              e.stopPropagation();
            }}
            aria-label={isAlreadyAddedToFavorites ? 'Remove from favorites' : 'Add to favorites'}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleFavorites(isAlreadyAddedToFavorites ? 'unlike' : 'like');
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <Icons name={isAlreadyAddedToFavorites ? 'favorite' : 'unfavorite'} />
          </div>
        </div>
      </AspectRatioWrapper>
      <div className={css.info}>
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(getReformedTitle(listing), {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>{' '}
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}
        </div>

        <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
      </div>
      <div className={css.measurements}>
        <div className={css.measurementLine}>
          <span>
            <FormattedMessage id="ListingCard.length" />:
          </span>{' '}
          {Length ? `${Length} in` : '—'}
        </div>
        <div className={css.measurementLine}>
          <span>
            <FormattedMessage id="ListingCard.width" />:
          </span>{' '}
          {Narrowest_Measurement && Widest_Measurement
            ? `${Narrowest_Measurement} - ${Widest_Measurement} in`
            : '—'}
        </div>
        <div className={css.measurementLine}>
          <span>
            <FormattedMessage id="ListingCard.thickness" />:
          </span>{' '}
          {thinknessLabel ? thinknessLabel : '—'}
        </div>
        {boardFootageLabel ? (
          <div className={css.measurementLine}>
            <span>Board ft:</span> {boardFootageLabel}
          </div>
        ) : null}
      </div>
    </NamedLink>
  );
};

export default ListingCard;
